import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  BsArrowLeft, BsArrowRepeat, BsSend, BsCheckCircle, BsXCircle, BsCloudUpload, BsPaperclip, BsTrash,
  BsRocketTakeoff, BsRobot, BsPerson, BsPeople, BsInfoCircle, BsBoxArrowUpRight
} from 'react-icons/bs';
import {
  STATUS, PRIORITY, StatusPill, SourceIcon, devApi, devUpload, openDevFile, formatDateTime, formatSize
} from '../components/ajustes/ajustesShared';

const AUTHOR = {
  cliente: { label: 'Cliente', icon: <BsPerson />, cls: 'border-primary' },
  claude: { label: 'Claude', icon: <BsRobot />, cls: 'border-info' },
  equipo: { label: 'Equipo', icon: <BsPeople />, cls: 'border-secondary' },
  sistema: { label: 'Sistema', icon: <BsInfoCircle />, cls: 'border-light' }
};
const KIND = {
  peticion: 'Petición',
  respuesta: 'Respuesta',
  nota: 'Nota',
  duda: 'Duda',
  aviso_staging: 'Aviso: listo para probar',
  aviso_publicado: 'Aviso: publicado',
  resultado: 'Resultado'
};
const JOB_KIND = { aplicar: 'Aplicar con Claude', publicar: 'Publicar' };
const JOB_STATUS = { pendiente: 'En cola', en_curso: 'En curso', completado: 'Completado', error: 'Error', cancelado: 'Cancelado' };
const CLOSED = ['publicado', 'hecho'];
const LIVE = ['en_cola', 'en_curso', 'publicando'];
// Estados desde los que el servidor acepta mandar el ajuste a Claude (igual que en el backend).
const QUEUE_FROM = ['pendiente', 'duda', 'esperando_cliente', 'respondido', 'en_staging', 'error', 'rechazado', 'manual'];

const AjusteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [edit, setEdit] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [note, setNote] = useState({ body: '', as: 'equipo', requeue: true });
  const [openLog, setOpenLog] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await devApi(`/${id}`);
      setData(d);
      setError('');
      setDrafts((prev) => {
        const next = {};
        for (const m of d.messages) {
          if (m.outbound && ['borrador', 'error'].includes(m.send_status)) next[m.id] = prev[m.id] !== undefined ? prev[m.id] : m.body;
        }
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Mientras el servidor trabaja, refresca solo para ver el resultado al momento.
  useEffect(() => {
    if (!data || !LIVE.includes(data.request.status)) return undefined;
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [data, load]);

  const run = async (key, fn, okText) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      await fn();
      if (okText) setNotice(okText);
      await load();
    } catch (err) {
      setError(err.message);
      await load();
    } finally {
      setBusy('');
    }
  };

  const r = data?.request;
  const conn = data?.connection;
  const connected = Boolean(r?.connected);
  // La nota solo puede reenviar el ajuste a Claude si el servidor lo aceptaría.
  const canNoteRequeue = Boolean(connected && r?.project_id && QUEUE_FROM.includes(r?.status));
  const totalCost = useMemo(() => (data?.jobs || []).reduce((n, j) => n + (Number(j.result?.coste_usd) || 0), 0), [data]);
  const outbox = useMemo(() => (data?.messages || []).filter((m) => m.outbound && ['borrador', 'error'].includes(m.send_status)), [data]);
  const timeline = useMemo(() => (data?.messages || []).filter((m) => !(m.outbound && ['borrador', 'error'].includes(m.send_status))), [data]);

  if (error && !data) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <div className="text-muted py-4">Cargando…</div>;

  const startEdit = () => setEdit({ title: r.title, description: r.description || '', priority: r.priority, project_id: r.project_id || '' });
  const saveEdit = () => run('edit', async () => {
    const body = { title: edit.title, description: edit.description, priority: edit.priority };
    const projectId = edit.project_id ? Number(edit.project_id) : null;
    if (projectId !== (r.project_id || null)) body.project_id = projectId;
    await devApi(`/${r.id}`, { method: 'PUT', body });
    setEdit(null);
  }, 'Guardado');

  const approve = () => run('approve', () => devApi(`/${r.id}/approve`, { method: 'POST' }),
    connected ? 'Enviado al servidor: Claude lo recogerá en unos minutos.' : 'Aprobado. El proyecto no tiene servidor: queda para hacerlo a mano.');
  const requeue = () => run('requeue', () => devApi(`/${r.id}/requeue`, { method: 'POST' }), 'Enviado de nuevo a Claude.');
  const publish = () => {
    if (!window.confirm('¿Publicar este ajuste en producción? El cliente verá el cambio en su app.')) return;
    run('publish', () => devApi(`/${r.id}/publish`, { method: 'POST' }), 'Publicación en cola: el servidor la hará en unos minutos.');
  };
  const cancelQueued = () => {
    if (!window.confirm(r.status === 'publicando' ? '¿Cancelar la publicación? El ajuste vuelve a staging.' : '¿Cancelar el envío a Claude? El ajuste vuelve a pendiente.')) return;
    run('cancel', () => devApi(`/${r.id}/cancel`, { method: 'POST' }), 'Cancelado.');
  };
  const reject = () => {
    const reason = window.prompt('¿Por qué se descarta? (opcional)');
    if (reason === null) return;
    run('reject', () => devApi(`/${r.id}/reject`, { method: 'POST', body: { reason } }), 'Ajuste descartado.');
  };
  const markDone = () => {
    const notifyClient = window.confirm('¿Avisar al cliente de que ya está hecho? (Aceptar = sí, Cancelar = no)');
    run('done', () => devApi(`/${r.id}/done`, { method: 'POST', body: { notifyClient } }), 'Marcado como hecho.');
  };
  const sendDraft = (m) => run(`send-${m.id}`, () => devApi(`/messages/${m.id}/send`, { method: 'POST', body: { body: drafts[m.id] } }), 'Mensaje enviado al cliente.');
  const discardDraft = (m) => {
    if (!window.confirm('¿Descartar este mensaje? No se enviará al cliente.')) return;
    run(`discard-${m.id}`, () => devApi(`/messages/${m.id}/discard`, { method: 'POST' }));
  };
  const sendNote = () => {
    if (!note.body.trim()) return;
    const requeueNote = note.requeue && canNoteRequeue;
    run('note', async () => {
      await devApi(`/${r.id}/messages`, { method: 'POST', body: { body: note.body, as: note.as, requeue: requeueNote } });
      setNote({ body: '', as: 'equipo', requeue: true });
    }, requeueNote ? 'Guardado y enviado a Claude.' : 'Guardado.');
  };
  const uploadFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    run('upload', () => devUpload(`/${r.id}/files`, file), 'Archivo añadido.');
  };
  const removeFile = (f) => {
    if (!window.confirm(`¿Quitar "${f.filename}" de este ajuste?`)) return;
    run(`rm-${f.id}`, () => devApi(`/${r.id}/files/${f.id}`, { method: 'DELETE' }));
  };

  const canApprove = ['pendiente', 'error', 'rechazado', 'manual'].includes(r.status) && (r.status !== 'manual' || connected);
  const canRequeue = connected && ['duda', 'esperando_cliente', 'respondido', 'en_staging'].includes(r.status);
  // Desde staging solo sin cambios de código: con commit, terminar es publicar.
  const canDone = ['manual', 'error', 'rechazado', 'pendiente', 'duda', 'esperando_cliente', 'respondido'].includes(r.status)
    || (r.status === 'en_staging' && !r.last_commit);
  // Un ajuste que rechazó Claude también se puede descartar (quita el aviso).
  const canReject = !CLOSED.includes(r.status) && r.status !== 'publicando' && (r.status !== 'rechazado' || r.staff_attention);
  const canCancel = ['en_cola', 'publicando'].includes(r.status);
  const canPublish = isAdmin && r.status === 'en_staging' && Boolean(r.last_commit);
  const statusInfo = STATUS[r.status] || {};

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button type="button" className="btn btn-link px-0" onClick={() => navigate('/cambios')}><BsArrowLeft className="me-1" /> Cambios</button>
        <span className="text-muted">/</span>
        <span className="text-muted">#{r.id}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={load} title="Actualizar"><BsArrowRepeat /></button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="row g-3">
        <div className="col-lg-8">
          {/* Cabecera y acciones */}
          <div className="card mb-3">
            <div className="card-body">
              {!edit ? (
                <>
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <h4 className="mb-1"><SourceIcon source={r.source} /> {r.title}</h4>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="small text-muted mb-2">
                    {r.client_name || 'Sin cliente'} · {r.project_title
                      ? <Link to={`/proyectos/${r.project_id}`}>{r.project_title}</Link>
                      : <span className="text-danger">sin proyecto</span>}
                    {' · '}Prioridad {PRIORITY[r.priority] || r.priority}
                    {' · '}{r.project_id ? (connected ? `servidor ${r.server_name || ''}` : 'sin servidor: se hace a mano') : ''}
                  </div>
                  {r.description ? <div style={{ whiteSpace: 'pre-wrap' }}>{r.description}</div> : <div className="text-muted small">Sin detalle.</div>}
                  <button type="button" className="btn btn-sm btn-link px-0 mt-1" onClick={startEdit}>Editar</button>
                </>
              ) : (
                <>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Qué hay que cambiar</label>
                    <input className="form-control" value={edit.title} onChange={(e) => setEdit((p) => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Detalle para Claude</label>
                    <textarea className="form-control" rows="6" value={edit.description} onChange={(e) => setEdit((p) => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-sm-8">
                      <label className="form-label small mb-1">Proyecto</label>
                      <select className="form-select" value={edit.project_id} disabled={LIVE.includes(r.status)} onChange={(e) => setEdit((p) => ({ ...p, project_id: e.target.value }))}>
                        <option value="">Sin proyecto</option>
                        {data.projects.map((p) => <option key={p.id} value={p.id}>{p.titulo}{p.estado === 'finalizado' ? ' (finalizado)' : ''}</option>)}
                      </select>
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label small mb-1">Prioridad</label>
                      <select className="form-select" value={edit.priority} onChange={(e) => setEdit((p) => ({ ...p, priority: e.target.value }))}>
                        {Object.entries(PRIORITY).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy === 'edit'} onClick={saveEdit}>Guardar</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEdit(null)}>Cancelar</button>
                  </div>
                </>
              )}
            </div>
            <div className="card-footer bg-light">
              {statusInfo.hint && <div className="small mb-2">{statusInfo.hint}</div>}
              {r.last_error && ['error', 'en_staging'].includes(r.status) && <div className="small text-danger mb-2">Último error: {r.last_error}</div>}
              {r.rejection_reason && r.status === 'rechazado' && <div className="small text-muted mb-2">Motivo: {r.rejection_reason}</div>}
              <div className="d-flex flex-wrap gap-2">
                {canApprove && (
                  <button type="button" className="btn btn-primary btn-sm d-flex align-items-center" disabled={!!busy || !r.project_id} title={!r.project_id ? 'Elige antes el proyecto' : ''} onClick={approve}>
                    <BsSend className="me-1" /> {connected ? (r.status === 'pendiente' ? 'Enviar a Claude' : 'Reintentar con Claude') : 'Aprobar (a mano)'}
                  </button>
                )}
                {canPublish && (
                  <button type="button" className="btn btn-success btn-sm d-flex align-items-center" disabled={!!busy} onClick={publish}>
                    <BsRocketTakeoff className="me-1" /> Publicar en producción
                  </button>
                )}
                {r.status === 'en_staging' && !r.last_commit && (
                  <span className="small text-muted align-self-center">Claude no cambió código: si está bien, márcalo como hecho.</span>
                )}
                {r.status === 'en_staging' && r.last_commit && !isAdmin && (
                  <span className="small text-muted align-self-center">Lo publica un administrador.</span>
                )}
                {r.status === 'en_staging' && conn?.staging_url && (
                  <a className="btn btn-outline-success btn-sm d-flex align-items-center" href={conn.staging_url} target="_blank" rel="noreferrer"><BsBoxArrowUpRight className="me-1" /> Ver staging</a>
                )}
                {canRequeue && (
                  <button type="button" className={`btn btn-sm d-flex align-items-center ${r.status === 'respondido' ? 'btn-primary' : 'btn-outline-primary'}`} disabled={!!busy} onClick={requeue}>
                    <BsArrowRepeat className="me-1" /> {r.status === 'respondido' ? 'Enviar a Claude con su respuesta' : 'Volver a Claude'}
                  </button>
                )}
                {canCancel && (
                  <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center" disabled={!!busy} onClick={cancelQueued}>
                    <BsXCircle className="me-1" /> {r.status === 'publicando' ? 'Cancelar publicación' : 'Cancelar envío'}
                  </button>
                )}
                {canDone && (
                  <button type="button" className="btn btn-outline-dark btn-sm d-flex align-items-center" disabled={!!busy} onClick={markDone}>
                    <BsCheckCircle className="me-1" /> Hecho a mano
                  </button>
                )}
                {canReject && (
                  <button type="button" className="btn btn-outline-danger btn-sm d-flex align-items-center ms-auto" disabled={!!busy} onClick={reject}>
                    <BsXCircle className="me-1" /> Descartar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mensajes al cliente pendientes de revisar */}
          {outbox.length > 0 && (
            <div className="card mb-3 border-warning">
              <div className="card-header bg-warning-subtle"><strong>Para el cliente</strong> <span className="small text-muted">· revisa y envía{data.clientMessagesAuto ? ' (el envío directo falló)' : ''}</span></div>
              <div className="card-body">
                {outbox.map((m) => (
                  <div key={m.id} className="mb-3">
                    <div className="small text-muted mb-1">
                      {KIND[m.kind] || m.kind} · por {m.channel === 'whatsapp' ? 'WhatsApp' : m.channel === 'email' ? 'email' : 'sin canal'}
                    </div>
                    {m.send_status === 'error' && m.send_error && <div className="alert alert-danger py-1 px-2 small mb-1">{m.send_error}</div>}
                    <textarea className="form-control mb-2" rows={Math.min(16, Math.max(5, String(drafts[m.id] ?? m.body).split('\n').length + 1))} value={drafts[m.id] ?? m.body} onChange={(e) => setDrafts((p) => ({ ...p, [m.id]: e.target.value }))} />
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-primary btn-sm d-flex align-items-center" disabled={!!busy || !m.channel} onClick={() => sendDraft(m)}><BsSend className="me-1" /> Enviar al cliente</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!!busy} onClick={() => discardDraft(m)}>Descartar</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conversación */}
          <div className="card mb-3">
            <div className="card-header"><strong>Conversación</strong></div>
            <div className="card-body">
              {!timeline.length && <div className="text-muted small">Sin mensajes.</div>}
              {timeline.map((m) => {
                const a = AUTHOR[m.author] || AUTHOR.sistema;
                return (
                  <div key={m.id} className={`border-start border-3 ${a.cls} ps-2 mb-3`}>
                    <div className="small text-muted d-flex flex-wrap gap-2">
                      <span>{a.icon} {a.label}{m.author === 'equipo' && m.author_email ? ` (${m.author_email})` : ''}</span>
                      <span>· {KIND[m.kind] || m.kind}</span>
                      {m.outbound && m.send_status === 'enviado' && <span>· enviado al cliente {m.channel === 'whatsapp' ? 'por WhatsApp' : 'por email'}</span>}
                      {m.outbound && m.send_status === 'descartado' && <span>· descartado</span>}
                      <span className="ms-auto">{formatDateTime(m.sent_at || m.created_at)}</span>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }} className={m.send_status === 'descartado' ? 'text-muted text-decoration-line-through' : ''}>{m.body}</div>
                  </div>
                );
              })}
              {!CLOSED.includes(r.status) && (
                <div className="border-top pt-3">
                  <textarea className="form-control mb-2" rows="3" placeholder={note.as === 'cliente' ? 'Lo que te ha dicho el cliente (por teléfono, en persona…)' : 'Indicaciones para Claude o notas internas'} value={note.body} onChange={(e) => setNote((p) => ({ ...p, body: e.target.value }))} />
                  <div className="d-flex flex-wrap gap-3 align-items-center">
                    <div className="btn-group btn-group-sm" role="group">
                      <button type="button" className={`btn ${note.as === 'equipo' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setNote((p) => ({ ...p, as: 'equipo' }))}>Nota del equipo</button>
                      <button type="button" className={`btn ${note.as === 'cliente' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setNote((p) => ({ ...p, as: 'cliente' }))}>Respuesta del cliente</button>
                    </div>
                    {canNoteRequeue && (
                      <div className="form-check mb-0">
                        <input className="form-check-input" type="checkbox" id="noteRequeue" checked={note.requeue} onChange={(e) => setNote((p) => ({ ...p, requeue: e.target.checked }))} />
                        <label className="form-check-label small" htmlFor="noteRequeue">Enviar a Claude con esto</label>
                      </div>
                    )}
                    <button type="button" className="btn btn-primary btn-sm ms-auto" disabled={!!busy || !note.body.trim()} onClick={sendNote}>Guardar</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {/* Archivos */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong><BsPaperclip /> Archivos</strong>
              <button type="button" className="btn btn-sm btn-outline-primary d-flex align-items-center" disabled={!!busy} onClick={() => fileInput.current?.click()}><BsCloudUpload className="me-1" /> Añadir</button>
              <input ref={fileInput} type="file" className="d-none" onChange={uploadFile} />
            </div>
            <ul className="list-group list-group-flush">
              {!data.files.length && <li className="list-group-item small text-muted">Sin archivos. Los adjuntos del email o WhatsApp del cliente se añaden solos.</li>}
              {data.files.map((f) => (
                <li key={f.id} className="list-group-item d-flex align-items-center gap-2">
                  <button type="button" className="btn btn-link p-0 text-start text-truncate" style={{ maxWidth: '75%' }} onClick={() => openDevFile(f.id, f.filename).catch((err) => setError(err.message))}>{f.filename}</button>
                  <span className="small text-muted">{formatSize(f.size)}</span>
                  <button type="button" className="btn btn-sm btn-link text-danger ms-auto p-0" title="Quitar" disabled={!!busy} onClick={() => removeFile(f)}><BsTrash /></button>
                </li>
              ))}
            </ul>
          </div>

          {/* Servidor */}
          <div className="card mb-3">
            <div className="card-header"><strong>Servidor</strong></div>
            <div className="card-body small">
              {!r.project_id && <div className="text-danger">Elige el proyecto (Editar) para poder enviarlo a Claude.</div>}
              {r.project_id && !conn && <div className="text-muted">Este proyecto no está conectado a ningún servidor. Conéctalo en <Link to="/cambios">Cambios → Proyectos conectados</Link> o hazlo a mano.</div>}
              {conn && (
                <>
                  <div>Servidor: <strong>{conn.server_name || 'sin asignar'}</strong>{!conn.active && ' (pausado)'}</div>
                  {conn.staging_url && <div>Staging: <a href={conn.staging_url} target="_blank" rel="noreferrer">{conn.staging_url}</a></div>}
                  {conn.production_url && <div>Producción: <a href={conn.production_url} target="_blank" rel="noreferrer">{conn.production_url}</a></div>}
                </>
              )}
              {r.last_commit && <div className="mt-2">Último commit: <code>{r.last_commit}</code>{r.last_environment ? ` (${r.last_environment})` : ''}</div>}
            </div>
          </div>

          {/* Historial de trabajos */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Trabajos en el servidor</strong>
              {totalCost > 0 && <span className="small text-muted" title="Coste estimado de Claude Code en este cambio (suma de los trabajos)">{totalCost.toFixed(2)} $</span>}
            </div>
            <ul className="list-group list-group-flush">
              {!data.jobs.length && <li className="list-group-item small text-muted">Todavía no se ha enviado al servidor.</li>}
              {data.jobs.map((j) => (
                <li key={j.id} className="list-group-item small">
                  <div className="d-flex justify-content-between">
                    <span>{JOB_KIND[j.kind] || j.kind}</span>
                    <span className={j.status === 'error' ? 'text-danger' : 'text-muted'}>{JOB_STATUS[j.status] || j.status}</span>
                  </div>
                  <div className="text-muted">{formatDateTime(j.created_at)}{j.finished_at ? ` → ${formatDateTime(j.finished_at)}` : ''}</div>
                  {j.result?.resumen && <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{j.result.resumen}</div>}
                  {j.result?.commit && <div>Commit <code>{j.result.commit}</code></div>}
                  {(j.result?.modelo || Number(j.result?.coste_usd) > 0) && (
                    <div className="text-muted">{j.result?.modelo ? j.result.modelo.replace('claude-', '').replace(/-/g, ' ') : ''}{Number(j.result?.coste_usd) > 0 ? ` · ${Number(j.result.coste_usd).toFixed(2)} $` : ''}</div>
                  )}
                  {j.log && (
                    <>
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpenLog(openLog === j.id ? null : j.id)}>{openLog === j.id ? 'Ocultar log' : 'Ver log'}</button>
                      {openLog === j.id && <pre className="bg-light p-2 mt-1 mb-0" style={{ maxHeight: 300, overflow: 'auto', fontSize: 11, whiteSpace: 'pre-wrap' }}>{j.log}</pre>}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="small text-muted">
            Creado {formatDateTime(r.created_at)}{r.approved_at ? ` · aprobado ${formatDateTime(r.approved_at)}` : ''}{r.published_at ? ` · terminado ${formatDateTime(r.published_at)}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AjusteDetailPage;
